import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target, CaretUp, CaretDown } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrendBadge = ({ trend }) => {
  if (trend === 'UP') return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-800" data-testid="trend-up">UP</span>;
  if (trend === 'DOWN') return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-800" data-testid="trend-down">DOWN</span>;
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600" data-testid="trend-stable">STABLE</span>;
};

const MomentumBadge = ({ label }) => {
  if (label === 'HEATING') return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-800">HEATING</span>;
  if (label === 'COOLING') return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">COOLING</span>;
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-500">FLAT</span>;
};

export default function Window18RollingWindow() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [windowSize, setWindowSize] = useState('20');
  const [sortConfig, setSortConfig] = useState({ key: 'pred_score', dir: 'desc' });

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/rolling-window`); setData(res.data); }
      catch { toast.error('Failed to fetch rolling window analysis'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSort = (key) => setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const SortIcon = ({ k }) => sortConfig.key !== k ? null : sortConfig.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;

  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 18</h1><p className="text-xs text-gray-500">Rolling Window Analysis</p></div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-500">Window:</span>
              <Select value={windowSize} onValueChange={setWindowSize}>
                <SelectTrigger className="w-32" data-testid="window-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">Last 10</SelectItem>
                  <SelectItem value="20">Last 20</SelectItem>
                  <SelectItem value="50">Last 50</SelectItem>
                  <SelectItem value="100">Last 100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

            <ExportButton data={data} filename="w18_rolling_window" />
          </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">

        <WindowNarrative windowId="W18" {...NARRATIVES.W18} className="mb-5" />
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <Tabs defaultValue="ball_1" className="space-y-4">
            <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
              {Object.entries(ballLabels).map(([k, l]) => (
                <TabsTrigger key={k} value={k} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid={`tab-${k}`}>{l}</TabsTrigger>
              ))}
            </TabsList>
            {Object.entries(ballLabels).map(([key, label]) => {
              const windowData = data.balls?.[key]?.[windowSize] || [];
              const upCount = windowData.filter(n => n.trending === 'UP').length;
              const downCount = windowData.filter(n => n.trending === 'DOWN').length;
              const stableCount = windowData.filter(n => n.trending === 'STABLE').length;
              const sorted = [...windowData].sort((a, b) => {
                let aV = a[sortConfig.key] || 0, bV = b[sortConfig.key] || 0;
                return sortConfig.dir === 'desc' ? bV - aV : aV - bV;
              });
              const topPredicted = new Set([...windowData].sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0)).slice(0, 5).map(n => n.number));
              return (
                <TabsContent key={key} value={key}>
                  <div className="flex gap-3 mb-4 text-xs font-bold">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded">TRENDING UP: {upCount}</span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded">TRENDING DOWN: {downCount}</span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded">STABLE: {stableCount}</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">Window: Last {windowSize} draws</span>
                  </div>
                  <div className="bg-white border border-gray-200 rounded overflow-hidden">
                    <div className="overflow-x-auto" data-testid="rolling-table">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            {[['number','#'],['recent_count',`R${windowSize}`],['h1',`1st${windowSize/2}`],['h2',`2nd${windowSize/2}`],['overall_count','Pos'],['delta','Pos Delta'],['cross_recent','XBall R'],['cross_delta','XB Delta'],['xb_h1',`XB 1st`],['xb_h2',`XB 2nd`],['pred_score','Score']].map(([k,l]) => (
                              <th key={k} className="px-2 py-2 text-[10px] uppercase text-gray-700 cursor-pointer hover:bg-gray-200 text-center" onClick={() => handleSort(k)}>{l} <SortIcon k={k} /></th>
                            ))}
                            <th className="px-2 py-2 text-[10px] uppercase text-gray-700 text-center">Momentum</th>
                            <th className="px-2 py-2 text-[10px] uppercase text-gray-700 text-center">XB Mom</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.map((n, idx) => (
                            <tr key={n.number} className={topPredicted.has(n.number) ? 'bg-blue-100' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={topPredicted.has(n.number) ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)'} : {}} data-testid={`roll-row-${n.number}`}>
                              <td className="px-2 py-1 border-b font-mono font-bold text-center">{n.number}</td>
                              <td className="px-2 py-1 border-b font-mono text-center font-bold">{n.recent_count}</td>
                              <td className={`px-2 py-1 border-b font-mono text-center ${n.h1 > n.h2 ? 'text-red-500' : n.h1 < n.h2 ? 'text-green-600' : 'text-gray-400'}`}>{n.h1}</td>
                              <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.h2 > n.h1 ? 'text-green-600' : n.h2 < n.h1 ? 'text-red-500' : 'text-gray-400'}`}>{n.h2}</td>
                              <td className="px-2 py-1 border-b font-mono text-center">{n.overall_count}</td>
                              <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.delta > 1 ? 'text-green-700' : n.delta < -1 ? 'text-red-600' : 'text-gray-400'}`}>{n.delta > 0 ? '+' : ''}{n.delta}%</td>
                              <td className="px-2 py-1 border-b font-mono text-center font-bold">{n.cross_recent}</td>
                              <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.cross_delta > 2 ? 'text-green-700' : n.cross_delta < -2 ? 'text-red-600' : 'text-gray-400'}`}>{n.cross_delta > 0 ? '+' : ''}{n.cross_delta}%</td>
                              <td className={`px-2 py-1 border-b font-mono text-center ${n.xb_h1 > n.xb_h2 ? 'text-red-500' : n.xb_h1 < n.xb_h2 ? 'text-green-600' : 'text-gray-400'}`}>{n.xb_h1}</td>
                              <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.xb_h2 > n.xb_h1 ? 'text-green-600' : n.xb_h2 < n.xb_h1 ? 'text-red-500' : 'text-gray-400'}`}>{n.xb_h2}</td>
                              <td className="px-2 py-1 border-b font-mono text-center font-bold text-sm">{n.pred_score}</td>
                              <td className="px-2 py-1 border-b text-center"><MomentumBadge label={n.momentum_label} /></td>
                              <td className="px-2 py-1 border-b text-center"><MomentumBadge label={n.xb_momentum_label} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Prediction */}
                  <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 mt-4 text-white">
                    <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Predicted Next {label} (Position + Cross-Ball + Momentum)</h3></div>
                    <p className="text-xs text-white/70 mb-4">Score = <strong>40% Position</strong> + <strong>15% Pos-Momentum</strong> + <strong>30% Cross-Ball</strong> + <strong>15% XB-Momentum</strong>. HEATING = accelerating in 2nd half. COOLING = decelerating.</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {[...windowData].sort((a, b) => b.pred_score - a.pred_score).slice(0, 10).map((n, idx) => (
                        <div key={n.number} className={`p-3 rounded-lg ${idx === 0 ? 'bg-sky-100 text-sky-900' : 'bg-white/10'}`} data-testid={`pred-${n.number}`}>
                          <div className="text-2xl font-mono font-bold text-center">{n.number}</div>
                          <div className="text-xs text-center mt-1 opacity-80">H1:{n.h1} H2:{n.h2} {n.momentum_label === 'HEATING' ? '🔥' : n.momentum_label === 'COOLING' ? '❄' : '—'}</div>
                          <div className="text-xs text-center opacity-60">XB: {n.xb_h1}→{n.xb_h2} {n.xb_momentum_label === 'HEATING' ? '🔥' : n.xb_momentum_label === 'COOLING' ? '❄' : ''}</div>
                          <div className="text-center font-bold mt-1">{n.pred_score}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        ) : null}
      </main>
    </div>
  );
}
